/**
 * DesignStep.tsx - Fundraiser Builder Step 3: Layout and Design
 *
 * This component handles the third step of the fundraiser builder where users
 * customize the visual design, layout, and appearance of their fundraiser.
 *
 * Key Features:
 * - Layout template selection with live preview
 * - Color scheme customization and theming
 * - Section visibility and arrangement controls
 * - Drag-and-drop section management
 * - Donation button styling and customization
 * - Live preview with real-time updates
 * - Responsive design preview options
 *
 * Design Controls:
 * - Layout Menu: Template selection and zone configuration
 * - Colors Menu: Color palette and custom color editing
 * - Sections Menu: Section visibility and arrangement
 * - Button Menu: Donation button styling and colors
 * - Preview: Live preview with drag-and-drop functionality
 *
 * Layout System:
 * - 5 layout templates with different zone configurations
 * - Drag-and-drop section arrangement
 * - Zone-based responsive design
 * - Section visibility controls
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: MobX store, design menu components, preview component
 */

import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Layout, Palette, Heart, Eye } from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { DesignPreview } from "./design/DesignPreview";
import { DesignLayoutMenu } from "./design/DesignLayoutMenu";
import { DesignSectionsMenu } from "./design/DesignSectionsMenu";
import {
  DesignColorsMenu,
  DesignColorsMenuRef,
} from "./design/DesignColorsMenu";
import { DesignButtonMenu } from "./design/DesignButtonMenu";
import { resetSectionsToLayoutDefaults } from "./design/DropZone";
import {
  layoutTemplates,
  colorSwatches,
  colorTemplates,
} from "./design/designConstants";
import { ColorSwatches, FundRaiserTemplateLayoutType } from "../../enums";
import {
  ColorModel,
  FundraiserCustomColorConfig,
  FundraiserDonationButtonColors,
  RGBA,
} from "../../models";

const DesignContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0;
  min-height: 100vh;
  height: auto;
  position: relative;
  overflow: visible;
`;

const FloatingSideNav = styled.div<{ $topPosition: number }>`
  position: fixed;
  left: 65px; /* Moved 5px further left from 70px */
  top: ${(props) =>
    props.$topPosition}px; /* Dynamic top position based on scroll */
  width: 100px;
  background: #f8fafc;
  border-radius: 12px;
  padding: 36px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  z-index: 999999;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transition: top 0.1s ease-out; /* Smooth transition when position changes */

  /* Position relative to the main content area */
  margin-left: calc(
    (100vw - 1400px) / 2
  ); /* Center relative to max-width container */

  /* Adjust for smaller desktop screens */
  @media (max-width: 1400px) {
    margin-left: 0; /* Remove centering on smaller screens */
    left: 95px; /* Adjusted for smaller screens (moved 5px left from 100px) */
  }
`;

const NavIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const NavIcon = styled.button<{ active: boolean }>`
  width: 54px;
  height: 54px;
  border: none;
  border-radius: 10px;
  background: ${(props) => (props.active ? "#10b981" : "transparent")};
  color: ${(props) => (props.active ? "#ffffff" : "#64748b")};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    transform: scale(2);
  }

  &:hover {
    background: ${(props) => (props.active ? "#059669" : "transparent")};
    color: ${(props) => (props.active ? "#ffffff" : "#10b981")};
  }
`;

const NavIconLabel = styled.div<{ active: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.active ? "#142937" : "#142937")};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
  transition: all 0.2s ease;
`;

const SubMenuContainer = styled.div<{
  isOpen: boolean;
  activeMenu: ActiveMenu;
  $topPosition: number;
}>`
  position: fixed;
  left: 177px; /* Positioned to the right of FloatingSideNav (65px + 100px + 12px gap) */
  top: ${(props) =>
    props.$topPosition}px; /* Match FloatingSideNav dynamic positioning */
  transform: translateX(${(props) => (props.isOpen ? "0" : "-220px")});
  width: 300px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border: 1px solid #e2e8f0;
  opacity: ${(props) => (props.isOpen ? "1" : "0")};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999998;
  max-height: calc(
    100vh - ${(props) => props.$topPosition + 20}px
  ); /* Dynamic max-height based on position */
  will-change: transform, opacity;

  /* Position relative to the main content area */
  margin-left: calc(
    (100vw - 1400px) / 2
  ); /* Center relative to max-width container */

  /* Adjust for smaller desktop screens */
  @media (max-width: 1400px) {
    margin-left: 0; /* Remove centering on smaller screens */
    left: 207px; /* Adjust for smaller screens (95px + 100px + 12px gap) */
  }
`;

const ClickOutsideOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999997;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  background: transparent;
`;

const SubMenuHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 26px 16px 16px;
  gap: 8px;
  background: #f8fafc;
  border-radius: 12px 12px 0 0;
`;

const SubMenuTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const PreviewContainer = styled.div`
  flex: 1;
  margin-left: 100px;
  padding: 0 0 0 20px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  background: transparent;
  min-height: 100vh;

  /* Ensure scroll doesn't interfere with fixed nav */
`;
type ActiveMenu = "layout" | "sections" | "colors" | "button" | null;

export const DesignStep: React.FC = observer(() => {
  const { builderData } = fundraiserStore;
  const [selectedLayoutId, setSelectedLayoutId] = useState(
    fundraiserStore.selectedFundraiserDetails.selectedLayout
  );
  const [activeMenu, setActiveMenu] = useState<ActiveMenu>(null); // No menu selected on initial load
  const [sideNavTopPosition, setSideNavTopPosition] = useState(285); // Dynamic top position

  // Handle scroll to adjust sidebar position
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const initialTop = 285; // Starting top position
      const minTop = 100; // Minimum top position (don't go higher than this)
      const maxScroll = 500; // After this much scroll, start reducing top position

      if (scrollY <= maxScroll) {
        // No adjustment needed, stay at initial position
        setSideNavTopPosition(initialTop);
      } else {
        // Calculate new top position: reduce as user scrolls down
        const scrollBeyondMax = scrollY - maxScroll;
        const reduction = Math.min(scrollBeyondMax * 0.3, initialTop - minTop); // Reduce by 30% of excess scroll
        const newTop = Math.max(minTop, initialTop - reduction);
        setSideNavTopPosition(newTop);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Initialize design data if not present
  const sectionVisibility = builderData.sectionVisibility || {
    header: true,
    fundraiserProgress: true,
    promoterProgress: true,
    stats: true,
    mission: true,
    impact: true,
    media: true,
    donation: true,
    promoters: true,
    recent: true,
    cta: true,
    description: false,
    progress: true,
  };
  const promoterCount = builderData.promoterCount || 5;
  const recentDonationsCount = builderData.recentDonationsCount || 10;
  // Determine whether a custom swatch is selected
  const isCustomSwatch =
    builderData.selectedColorSwatch === ColorSwatches.Custom;
  const hasCustomColors = isCustomSwatch && !!builderData.customColors;
  const selectedColorSwatch =
    builderData.selectedColorSwatch || ColorSwatches.Default;

  const showDonationComments = builderData.showDonationComments || false;
  const donationButtonType = builderData.donationButtonType || "gradient";
  const donationButtonColors =
    builderData.donationButtonColors || new FundraiserDonationButtonColors();

  const handleLayoutSelect = (layout: any) => {
    setSelectedLayoutId(layout.id);
    fundraiserStore.updateBuilderField("selectedLayout", layout.id);

    // Clear all zones before resetting to new layout defaults
    const newLayoutZones = { ...fundraiserStore.layoutZones };
    Object.keys(newLayoutZones).forEach((zoneKey) => {
      newLayoutZones[zoneKey as keyof typeof newLayoutZones] = [];
    });

    fundraiserStore.layoutZones = newLayoutZones;

    // Reset sections to the new layout's defaults
    resetSectionsToLayoutDefaults(layout.id);
  };

  const handleMenuClick = (menu: ActiveMenu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  const handleSectionToggle = (sectionId: string) => {
    if (
      sectionId === "fundraiserProgress" ||
      sectionId === "promoterProgress"
    ) {
      // Handle progress sections specially
      const updated = {
        ...sectionVisibility,
        [sectionId]: !sectionVisibility[sectionId],
      };
      fundraiserStore.updateBuilderField("sectionVisibility", updated);
    } else {
      // Handle other sections normally
      const updated = {
        ...sectionVisibility,
        [sectionId]: !sectionVisibility[sectionId],
      };
      fundraiserStore.updateBuilderField("sectionVisibility", updated);
    }
  };

  const handlePromoterCountChange = (increment: boolean) => {
    let newCount;
    if (increment) {
      newCount = promoterCount >= 10 ? 3 : promoterCount + 1;
    } else {
      newCount = promoterCount <= 3 ? 10 : promoterCount - 1;
    }
    fundraiserStore.updateBuilderField("promoterCount", newCount);
  };

  const handleRecentDonationsCountChange = (increment: boolean) => {
    let newCount;
    if (increment) {
      newCount = recentDonationsCount >= 25 ? 5 : recentDonationsCount + 1;
    } else {
      newCount = recentDonationsCount <= 5 ? 25 : recentDonationsCount - 1;
    }
    fundraiserStore.updateBuilderField("recentDonationsCount", newCount);
  };

  const handleShowCommentsToggle = () => {
    fundraiserStore.updateBuilderField(
      "showDonationComments",
      !showDonationComments
    );
  };

  const handleProgressFundraiserToggle = () => {
    const currentValue =
      builderData.sectionVisibility?.fundraiserProgress !== false;
    const updated = {
      ...sectionVisibility,
      fundraiserProgress: !currentValue,
    };
    fundraiserStore.updateBuilderField("sectionVisibility", updated);

    // Handle zone placement
    const selectedLayoutId =
      builderData.selectedLayout || FundRaiserTemplateLayoutType.Layout1;
    const { handleSectionVisibilityChange } = require("./design/DropZone");
    handleSectionVisibilityChange(
      "fundraiserProgress",
      !currentValue,
      selectedLayoutId
    );
  };

  const handleProgressPromoterToggle = () => {
    const currentValue =
      builderData.sectionVisibility?.promoterProgress !== false;
    const updated = {
      ...sectionVisibility,
      promoterProgress: !currentValue,
    };
    fundraiserStore.updateBuilderField("sectionVisibility", updated);

    // Handle zone placement
    const { handleSectionVisibilityChange } = require("./design/DropZone");
    const selectedLayoutId =
      builderData.selectedLayout || FundRaiserTemplateLayoutType.Layout1;
    handleSectionVisibilityChange(
      "promoterProgress",
      !currentValue,
      selectedLayoutId
    );
  };

  const handleProgressLayoutChange = (layout: "side-by-side" | "stacked") => {
    fundraiserStore.updateBuilderField("progressLayout", layout);
  };

  const handleColorSwatchSelect = (swatchId: keyof typeof ColorSwatches) => {
    // Handle custom colors selection
    if (swatchId === ColorSwatches.Custom) {
      fundraiserStore.updateBuilderField(
        "selectedColorSwatch",
        ColorSwatches.Custom
      );
      return;
    }

    // First try to find in new colorTemplates
    const template = colorTemplates.find((t) => t.id === swatchId);
    if (template) {
      fundraiserStore.updateBuilderField("selectedColorSwatch", swatchId);
      fundraiserStore.updateBuilderField("customColors", template.colors);

      // Update donation button colors based on current donation button type
      fundraiserStore.updateBuilderField("donationButtonColors", {
        primary: template.colors.primary,
        secondary: template.colors.secondary,
        text: new ColorModel({
          hex: "#ffffff",
          rgba: "rgba(255, 255, 255, 1)",
          rgbValue: new RGBA({ r: "255", g: "255", b: "255", a: "1" }),
        }),
      });
      return;
    }

    // Fallback to legacy colorSwatches for backward compatibility
    const swatch = colorSwatches.find((s) => s.id === swatchId);
    if (swatch) {
      fundraiserStore.updateBuilderField("selectedColorSwatch", swatchId);
      fundraiserStore.updateBuilderField("customColors", {
        primary: swatch.primary,
        secondary: swatch.secondary,
        accent: swatch.accent,
        text: swatch.text,
        background: swatch.background,
      });

      // Update donation button colors based on current donation button type
      fundraiserStore.updateBuilderField("donationButtonColors", {
        primary: swatch.primary,
        secondary: swatch.secondary,
        text: new ColorModel({
          hex: "#ffffff",
          rgba: "rgba(255, 255, 255, 1)",
          rgbValue: new RGBA({ r: "255", g: "255", b: "255", a: "1" }),
        }),
      });
    }
  };

  const handleCustomColorPreview = (colorKey: string, color: string) => {
    // Preview handler that only updates colors, not hasCustomColors
    // This prevents re-renders that close the color picker during customize mode
    fundraiserStore.updateBuilderField("customColors", {
      ...builderData.getCustomColors(),
      [colorKey]: color,
    });
  };

  const handleDonationButtonTypeChange = (type: string) => {
    fundraiserStore.updateBuilderField("donationButtonType", type);
    const template = colorTemplates.find(
      (t) => t.id === builderData.selectedColorSwatch
    );
    if (template) {
      fundraiserStore.updateBuilderField("donationButtonColors", {
        primary: template.colors.primary,
        secondary: template.colors.secondary,
        text: new ColorModel({
          hex: "#ffffff",
          rgba: "rgba(255, 255, 255, 1)",
          rgbValue: new RGBA({ r: "255", g: "255", b: "255", a: "1" }),
        }),
      });
    }
  };

  const handleDonationButtonColorChange = (
    colorKey: keyof FundraiserDonationButtonColors,
    color: ColorModel
  ) => {
    fundraiserStore.updateBuilderField("donationButtonColors", {
      ...donationButtonColors,
      [colorKey]: color,
    });
  };

  const renderSubMenuContent = () => {
    switch (activeMenu) {
      case "layout":
        return (
          <DesignLayoutMenu
            selectedLayoutId={selectedLayoutId}
            onLayoutSelect={handleLayoutSelect}
          />
        );

      case "sections":
        return (
          <DesignSectionsMenu
            sectionVisibility={sectionVisibility}
            promoterCount={promoterCount}
            recentDonationsCount={recentDonationsCount}
            showDonationComments={showDonationComments}
            progressLayout={builderData.progressLayout || "side-by-side"}
            onSectionToggle={handleSectionToggle}
            onPromoterCountChange={handlePromoterCountChange}
            onRecentDonationsCountChange={handleRecentDonationsCountChange}
            onShowCommentsToggle={handleShowCommentsToggle}
            onProgressFundraiserToggle={handleProgressFundraiserToggle}
            onProgressPromoterToggle={handleProgressPromoterToggle}
            onProgressLayoutChange={handleProgressLayoutChange}
            builderData={builderData}
          />
        );

      case "colors":
        return (
          <DesignColorsMenu
            selectedColorSwatch={selectedColorSwatch}
            customColors={builderData.getCustomColors()}
            hasCustomColors={hasCustomColors}
            onColorSwatchSelect={handleColorSwatchSelect}
            onCustomColorPreview={handleCustomColorPreview}
          />
        );

      case "button":
        return (
          <DesignButtonMenu
            donationButtonType={donationButtonType}
            donationButtonColors={donationButtonColors}
            onDonationButtonTypeChange={handleDonationButtonTypeChange}
            onDonationButtonColorChange={handleDonationButtonColorChange}
          />
        );

      default:
        return null;
    }
  };

  const getSubMenuTitle = () => {
    switch (activeMenu) {
      case "layout":
        return "Layouts";
      case "sections":
        return "Sections";
      case "colors":
        return "Colors";
      case "button":
        return "Donate Button";
      default:
        return "";
    }
  };

  return (
    <>
      {/* Render floating navigation outside FormCard using portal */}
      {createPortal(
        <>
          {/* Click outside overlay */}
          <ClickOutsideOverlay
            isOpen={activeMenu !== null}
            onClick={() => setActiveMenu(null)}
          />

          {/* Floating Side Navigation */}
          <FloatingSideNav $topPosition={sideNavTopPosition} data-floating-nav>
            <NavIconContainer>
              <NavIcon
                active={activeMenu === "layout"}
                onClick={() => handleMenuClick("layout")}
                title="Layouts"
              >
                <Layout size={16} />
              </NavIcon>
              <NavIconLabel active={activeMenu === "layout"}>
                LAYOUTS
              </NavIconLabel>
            </NavIconContainer>

            <NavIconContainer>
              <NavIcon
                active={activeMenu === "sections"}
                onClick={() => handleMenuClick("sections")}
                title="Sections"
              >
                <Eye size={16} />
              </NavIcon>
              <NavIconLabel active={activeMenu === "sections"}>
                SECTIONS
              </NavIconLabel>
            </NavIconContainer>

            <NavIconContainer>
              <NavIcon
                active={activeMenu === "colors"}
                onClick={() => handleMenuClick("colors")}
                title="Colors"
              >
                <Palette size={16} />
              </NavIcon>
              <NavIconLabel active={activeMenu === "colors"}>
                COLORS
              </NavIconLabel>
            </NavIconContainer>

            <NavIconContainer>
              <NavIcon
                active={activeMenu === "button"}
                onClick={() => handleMenuClick("button")}
                title="Donate Button"
              >
                <Heart size={16} />
              </NavIcon>
              <NavIconLabel active={activeMenu === "button"}>
                BUTTON
              </NavIconLabel>
            </NavIconContainer>
          </FloatingSideNav>

          {/* Animated Sub-Menu */}
          <SubMenuContainer
            isOpen={activeMenu !== null}
            activeMenu={activeMenu}
            $topPosition={sideNavTopPosition}
            data-sub-menu
          >
            {activeMenu && (
              <>
                <SubMenuHeader>
                  <SubMenuTitle style={{ paddingTop: "10px" }}>
                    {getSubMenuTitle()}
                  </SubMenuTitle>
                </SubMenuHeader>
                {renderSubMenuContent()}
              </>
            )}
          </SubMenuContainer>
        </>,
        document.body
      )}

      {/* Main design container without floating nav */}
      <DesignContainer>
        {/* Preview - shifted to accommodate side nav */}
        <PreviewContainer>
          <DesignPreview />
        </PreviewContainer>
      </DesignContainer>
    </>
  );
});
